import { AxiosError } from 'axios';
import { f7 } from 'framework7-react';
import { useState } from 'react';
import { customerInfoApi } from '@/api/customerApi';
import { paymentMethodChangeApi } from '@/api/warrantyApi';
import { store } from '@/config/store';
import { useToast } from '@/hooks/useToast';
import { PaymentMethodChangeParams } from '@/types/api/warrantyApi';
import { storeDispatch } from '@/utils/store';

const NEXT_WARRANTY_BRANCH_INCREMENT = 1;

export const usePaymentMethodChange = () => {
  const [error, setError] = useState<AxiosError>();
  const { openToast } = useToast();
  const changePaymentMethod = async (
    params: PaymentMethodChangeParams,
  ): Promise<boolean> => {
    try {
      f7.preloader.show();
      const res = await paymentMethodChangeApi(params);
      if (!res.data.success) {
        openToast(
          res.data.error?.message ?? '決済方法の変更に失敗しました',
          'toast-failed',
        );
        return false;
      }

      const params2 = { m_customer_id: store.state.customerInfo.id };
      const customerInfoResponse = await customerInfoApi(params2);
      if (params.warranty_no) {
        const carId = store.state.carItemInfo.m_car_id;
        const car = customerInfoResponse.data.cars?.find(
          (candidateCar) => candidateCar.m_car_id === carId,
        );
        if (car) {
          const restoredCurrentWarranty = car.warranties?.find(
            (warranty) =>
              warranty.warranty_no === params.warranty_no &&
              (params.branch_no === undefined ||
                warranty.branch_no === Number(params.branch_no)),
          );
          await Promise.all([
            storeDispatch(
              'setCarsInfo',
              'carsInfo',
              customerInfoResponse.data.cars,
            ),
            storeDispatch('setCarItemInfo', 'carItemInfo', car),
            storeDispatch('setWarranties', 'warranties', car.warranties),
            storeDispatch(
              'setNextWarranties',
              'nextWarranties',
              car.next_warranty,
            ),
            storeDispatch(
              'setCurrentWarranty',
              'currentWarranty',
              restoredCurrentWarranty ?? car.warranties?.[0],
            ),
            storeDispatch(
              'setNextWarranty',
              'nextWarranty',
              car.next_warranty?.find(
                (nextWarranty) =>
                  nextWarranty.warranty_no === params.warranty_no &&
                  (params.branch_no === undefined ||
                    nextWarranty.branch_no ===
                      Number(params.branch_no) +
                        NEXT_WARRANTY_BRANCH_INCREMENT),
              ) ?? car.next_warranty?.[0],
            ),
          ]);
        }
      }
      await Promise.all([
        storeDispatch(
          'setPaymentMethodsInfo',
          'paymentMethodsInfo',
          customerInfoResponse.data.payment_methods,
        ),
        storeDispatch(
          'setBillsInfo',
          'billsInfo',
          customerInfoResponse.data.bill_infomations,
        ),
        storeDispatch(
          'setAdvancePaymentsInfo',
          'advancePaymentsInfo',
          customerInfoResponse.data.prepaids,
        ),
      ]);
      openToast('決済方法を変更しました。');
      return true;
    } catch (err) {
      setError(err as AxiosError);
      openToast('決済方法の変更に失敗しました', 'toast-failed');
      return false;
    } finally {
      f7.preloader.hide();
    }
  };

  return {
    changePaymentMethod,
    error,
  };
};
